import qc from 'qc'
import nodePool from '@ps/bridge/qc/node-pool'
import _ from 'lodash'
import { isText } from '@ps/bridge/qc/base'
import gameIframe from '@ps/bridge/iframe/iframe'
import {
  EditorDataScript,
  getScript,
  nodeOrigin,
} from '@ps/bridge/qc/scripts/editor'
import i18n from '@ps/lang'
import { scripts } from '@ps/bridge/qc/scripts'

/**
 * 查找节点树上的的后代节点中匹配到的第一个节点
 */
export function find(
  node: qc.Node,
  callback: (item: qc.Node) => boolean,
): qc.Node | undefined {
  if (callback(node)) {
    return node
  } else if (node.children && node.children.length > 0) {
    for (let index = 0; index < node.children.length; index++) {
      const current = find(node.children[index], callback)
      if (current) return current
    }
  }
}

/**
 * 根据uuid在节点树上查找节点
 */
export function findByUuid(uuid: string): qc.Node | undefined {
  const node = nodePool.find(uuid)

  let current = node
  while (current && current.parent) {
    // 可以找到根节点，说明节点还在节点树上
    if (current.parent.name === 'UIRoot') {
      return node
    }
    current = current.parent
  }
}

/**
 * 获取节点所有后代节点，包括节点自身，使用先序遍历，不要改动遍历方式
 */
export function getDescendants(
  node: qc.Node,
  callback?: ((item: qc.Node) => boolean) | null | boolean,
  includeSelf = true,
): qc.Node[] {
  const nodes: qc.Node[] = []

  // 第一个参数如果是布尔值，则将其当做 includeSelf 使用
  if (_.isBoolean(callback)) {
    includeSelf = callback
  }

  // 后序遍历，保证顺序正确
  const rec = (qcNode: qc.Node) => {
    if (!includeSelf && qcNode.uuid === node.uuid) {
      // console.log('忽略自身')
    } else {
      if (_.isFunction(callback)) {
        if (callback(qcNode)) {
          nodes.push(qcNode)
        }
      } else {
        nodes.push(qcNode)
      }
    }
    if (qcNode && qcNode.children && qcNode.children.length > 0) {
      _.each(qcNode.children, (item) => {
        item && rec(item)
      })
    }
  }

  rec(node)
  return nodes
}

/**
 * 获取祖先节点，不包括节点自身
 * @param node
 */
export function getAncestor(node: qc.Node): qc.Node[] {
  const nodes = []
  while (node.parent) {
    nodes.push(node.parent)
    node = node.parent
  }
  return nodes
}

export function getDescendantsText(
  node: qc.Node,
  includeSelf = true,
): qc.UIText[] {
  return getDescendants(
    node,
    (node) => isText(node),
    includeSelf,
  ) as qc.UIText[]
}

/**
 * 查找节点的后代节点中是否有符合条件的节点
 */
export function some(
  node: qc.Node,
  callback: (item: qc.Node) => boolean,
): boolean {
  const list: qc.Node[] = getDescendants(node)
  return list.some(callback)
}

/**
 * 查找节点的后代节点中是否所有节点都符合条件
 */
export function every(
  node: qc.Node,
  callback: (item: qc.Node) => boolean,
): boolean {
  const list: qc.Node[] = getDescendants(node)
  return list.every(callback)
}

/**
 * 查找节点的后代节点中符合条件的节点
 */
export function filter(
  node: qc.Node,
  callback: (item: qc.Node) => boolean,
): qc.Node[] {
  const list: qc.Node[] = getDescendants(node)
  return list.filter(callback)
}

/**
 * 循环并返回后代节点
 */
export function map(
  node: qc.Node,
  callback: (item: qc.Node) => qc.Node,
): qc.Node[] {
  const list: qc.Node[] = getDescendants(node)
  return list.map(callback)
}

/**
 * 返回当前节点的层级
 * @param node
 */
export function getNodeLvl(node: qc.Node): number {
  const gamePlay = gameIframe.gamePlay
  let lvl = 0
  if (!node.parent) {
    return lvl
  }
  let parent = node.parent
  // 最多循环 30 次
  while (parent !== gamePlay && lvl < 30) {
    if (parent.parent) {
      parent = parent.parent
    } else {
      parent = gamePlay
    }
    lvl++
  }
  return lvl
}

/**
 * 遍历树节点
 */
export function each(node: qc.Node, callback: (item: qc.Node) => void): void {
  if (node) {
    callback(node)
    if (node.children && node.children.length > 0) {
      _.each(node.children, (item) => {
        each(item, callback)
      })
    }
  }
}

/**
 * 根据新版 PS 的要求，重置 node 节点的默认 Layout
 * @param node
 */
export function resetNodeDefaultLayout(node: qc.Node): void {
  if (
    node.pivotX !== 0.5 ||
    node.pivotY !== 0.5 ||
    node.anchoredX !== 0 ||
    node.anchoredY !== 0
  ) {
    node.pivotX = node.pivotY = 0.5
    node.minAnchor.x =
      node.minAnchor.y =
      node.maxAnchor.x =
      node.maxAnchor.y =
        0.5
    node.x = gameIframe.UIRoot.width / 2
    node.y = gameIframe.UIRoot.height / 2
    node.anchoredX = node.anchoredY = 0
  }
  const config = getScript(node) as EditorDataScript
  config.$data.nodeOrigin = nodeOrigin.local
  console.log(`当前${node.name}节点的类型是${nodeOrigin.local}`)
}

export function generateEmptyNode(targetNode: qc.Node, gameOb: any) {
  const res = gameOb.assets.find('__builtin_resource__')
  const emptyTexture = res.getTexture('empty.png')
  const node = gameOb.add.image(targetNode)
  resetNodeDefaultLayout(node)
  node.texture = emptyTexture
  node.name = i18n.t('workbench.animation.scriptManager.handArea')
  node.addScript(scripts.emptyNode.name)
  return node
}
